import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Calendar, Utensils, Pause, Play, X, RefreshCw, Home, LogOut, User, AlertCircle, CheckCircle2, Edit2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import AppFooter from '@/components/AppFooter';
import MealSwapModal from '@/components/MealSwapModal';

const API = process.env.REACT_APP_BACKEND_URL;

const Subscriptions = () => {
  const { logout } = useAuth();
  const [subscriptions, setSubscriptions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedSubscription, setSelectedSubscription] = useState(null);
  const [showSwapModal, setShowSwapModal] = useState(false);
  const [mealToSwap, setMealToSwap] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    fetchSubscriptions();
  }, []);

  const fetchSubscriptions = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/subscriptions`, {
        withCredentials: true
      });
      
      if (response.data.success) {
        const subs = response.data.subscriptions || [];
        setSubscriptions(subs);
        if (subs.length > 0 && !selectedSubscription) {
          setSelectedSubscription(subs[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
      toast.error('Failed to load subscriptions');
    } finally {
      setLoading(false);
    }
  };

  const handlePause = async (subId) => {
    try {
      const weeks = prompt('How many weeks would you like to pause? (Max 4 weeks)', '2');
      if (!weeks) return;
      
      const pauseWeeks = parseInt(weeks);
      if (pauseWeeks < 1 || pauseWeeks > 4) {
        toast.error('Please enter 1-4 weeks');
        return;
      }

      const response = await axios.put(
        `${API}/api/subscriptions/${subId}/pause?pause_weeks=${pauseWeeks}`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success(`Subscription paused for ${pauseWeeks} week(s)`);
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to pause subscription');
    }
  };

  const handleResume = async (subId) => {
    try {
      const response = await axios.put(
        `${API}/api/subscriptions/${subId}/resume`,
        {},
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Subscription resumed!');
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to resume subscription');
    }
  };

  const handleCancel = async (subId) => {
    if (!window.confirm('Are you sure? Your current week will still be delivered, but no future deliveries will be scheduled.')) {
      return;
    }

    try {
      const response = await axios.delete(
        `${API}/api/subscriptions/${subId}`,
        { withCredentials: true }
      );

      if (response.data.success) {
        toast.success('Subscription cancelled');
        fetchSubscriptions();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel subscription');
    }
  };

  const openSwapModal = (meal) => {
    setMealToSwap(meal);
    setShowSwapModal(true);
  };

  const handleSwapSuccess = () => {
    fetchSubscriptions();
  };

  const WeeklyMealPlan = ({ subscription }) => {
    const currentPlan = subscription.current_week_plan;
    
    if (!currentPlan || !currentPlan.meals) {
      return (
        <Card>
          <CardContent className="p-8 text-center">
            <Calendar className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <p className="text-gray-600">No meal plan available yet</p>
          </CardContent>
        </Card>
      );
    }

    const canCustomize = currentPlan.can_customize !== false;
    const deadlineDate = new Date(currentPlan.customization_deadline);
    const deliveryDate = new Date(currentPlan.delivery_date);

    // Group meals by day
    const mealsByDay = currentPlan.meals.reduce((acc, meal) => {
      if (!acc[meal.day]) acc[meal.day] = [];
      acc[meal.day].push(meal);
      return acc;
    }, {});

    const daysOrder = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];

    return (
      <div className="space-y-4">
        {/* Plan Info */}
        <Card className="bg-gradient-to-r from-indigo-50 to-purple-50 border-indigo-200">
          <CardContent className="p-4">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-sm font-medium text-indigo-900">Week of {new Date(currentPlan.week_start_date).toLocaleDateString()}</p>
                <p className="text-xs text-indigo-700 mt-1">
                  Delivery: {deliveryDate.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                </p>
              </div>
              <div className="text-right">
                <Badge className={canCustomize ? 'bg-green-500' : 'bg-gray-400'}>
                  {canCustomize ? `✏️ Can Customize` : '🔒 Locked'}
                </Badge>
                {canCustomize && (
                  <p className="text-xs text-gray-600 mt-1">
                    Until {deadlineDate.toLocaleDateString()}
                  </p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Meals by Day */}
        {daysOrder.map(day => {
          const dayMeals = mealsByDay[day];
          if (!dayMeals) return null;

          return (
            <Card key={day}>
              <CardHeader className="pb-3">
                <CardTitle className="text-base capitalize font-semibold text-gray-800">
                  {day}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {dayMeals.map((meal, idx) => (
                  <div key={idx} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <h4 className="font-medium text-gray-900">{meal.meal_name}</h4>
                        {meal.is_customized && (
                          <Badge variant="secondary" className="text-xs bg-purple-100 text-purple-700">
                            <Edit2 className="h-3 w-3 mr-1" />
                            Customized
                          </Badge>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 capitalize mb-1">
                        {meal.meal_type} • {meal.cuisine_type?.replace('_', ' ')}
                      </p>
                      <div className="flex gap-3 text-xs text-gray-600">
                        <span>{meal.calories} cal</span>
                        <span>{meal.protein}g protein</span>
                        <span>{meal.carbs || 0}g carbs</span>
                      </div>
                      {meal.vendor_name && (
                        <p className="text-xs text-indigo-600 mt-1">
                          From: {meal.vendor_name}
                        </p>
                      )}
                    </div>
                    
                    {canCustomize && meal.is_swappable && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openSwapModal(meal)}
                        className="ml-4"
                      >
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Swap
                      </Button>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          );
        })}

        <Card className="bg-blue-50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-blue-900">
                <p className="font-medium mb-1">Customization Tips:</p>
                <ul className="list-disc list-inside space-y-1 text-blue-800">
                  <li>You can swap meals until 3 days before delivery</li>
                  <li>All swap options match your nutritional goals</li>
                  <li>Swaps are unlimited (before the deadline)</li>
                  <li>Meals will be delivered from selected vendors</li>
                </ul>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
        <nav className="bg-white/80 backdrop-blur-sm border-b sticky top-0 md:top-16 z-50">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-16">
              <Link to="/dashboard" className="text-xl font-bold text-indigo-600">HealthLoop</Link>
            </div>
          </div>
        </nav>
        <div className="flex justify-center items-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600 mx-auto"></div>
            <p className="mt-4 text-gray-600">Loading subscriptions...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50">
      {/* Navigation */}
      <nav className="bg-white/80 backdrop-blur-sm border-b sticky top-0 md:top-16 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/dashboard" className="text-xl font-bold text-indigo-600">HealthLoop</Link>
            <div className="flex items-center gap-4">
              <Link to="/dashboard">
                <Button variant="ghost" size="sm"><Home className="w-4 h-4 mr-2" />Dashboard</Button>
              </Link>
              <Link to="/nutrition">
                <Button variant="ghost" size="sm">Browse Meals</Button>
              </Link>
              <Link to="/orders">
                <Button variant="ghost" size="sm">Orders</Button>
              </Link>
              <Link to="/profile">
                <Button variant="ghost" size="sm"><User className="w-4 h-4 mr-2" />Profile</Button>
              </Link>
              <Button variant="ghost" size="sm" onClick={logout}><LogOut className="w-4 h-4 mr-2" />Logout</Button>
            </div>
          </div>
        </div>
      </nav>

      <div className="max-w-6xl mx-auto p-6">
        <div className="mb-6">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">My Subscriptions</h1>
          <p className="text-gray-600">Manage your meal plans and customize your weekly deliveries</p>
        </div>

        {subscriptions.length === 0 ? (
          <Card>
            <CardContent className="p-12 text-center">
              <Utensils className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-semibold mb-2">No Active Subscriptions</h3>
              <p className="text-gray-600 mb-4">Start a subscription plan to get weekly meal deliveries!</p>
              <Button onClick={() => navigate('/nutrition')} className="bg-indigo-600 hover:bg-indigo-700">
                Browse Meal Plans
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Subscription List */}
            <div className="lg:col-span-1">
              <Card>
                <CardHeader>
                  <CardTitle>Your Plans</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {subscriptions.map(sub => (
                    <div
                      key={sub.id}
                      onClick={() => setSelectedSubscription(sub)}
                      className={`p-4 rounded-lg border-2 cursor-pointer transition-all ${
                        selectedSubscription?.id === sub.id
                          ? 'border-indigo-500 bg-indigo-50'
                          : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        <Badge className={
                          sub.status === 'active' ? 'bg-green-500' :
                          sub.status === 'paused' ? 'bg-yellow-500' :
                          'bg-gray-500'
                        }>
                          {sub.status}
                        </Badge>
                        <p className="text-xs text-gray-500 capitalize">{sub.tier}</p>
                      </div>
                      <p className="font-semibold text-gray-900 capitalize">
                        {sub.goal.replace('_', ' ')} Plan
                      </p>
                      <p className="text-sm text-gray-600">
                        {sub.meals_per_week} meals/week
                      </p>
                      <p className="text-lg font-bold text-indigo-600 mt-2">
                        ${sub.price_per_week}/week
                      </p>
                    </div>
                  ))}
                </CardContent>
              </Card>

              {/* Actions */}
              {selectedSubscription && (
                <Card className="mt-4">
                  <CardHeader>
                    <CardTitle className="text-base">Manage</CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-2">
                    {selectedSubscription.status === 'active' && (
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={() => handlePause(selectedSubscription.id)}
                      >
                        <Pause className="w-4 h-4 mr-2" />
                        Pause Subscription
                      </Button>
                    )}
                    
                    {selectedSubscription.status === 'paused' && (
                      <Button
                        variant="outline"
                        className="w-full bg-green-50 border-green-200 text-green-700 hover:bg-green-100"
                        onClick={() => handleResume(selectedSubscription.id)}
                      >
                        <Play className="w-4 h-4 mr-2" />
                        Resume Subscription
                      </Button>
                    )}
                    
                    {selectedSubscription.status !== 'cancelled' && (
                      <Button
                        variant="outline"
                        className="w-full text-red-600 border-red-200 hover:bg-red-50"
                        onClick={() => handleCancel(selectedSubscription.id)}
                      >
                        <X className="w-4 h-4 mr-2" />
                        Cancel Subscription
                      </Button>
                    )}
                  </CardContent>
                </Card>
              )}
            </div>

            {/* Weekly Meal Plan */}
            <div className="lg:col-span-2">
              {selectedSubscription && (
                <WeeklyMealPlan subscription={selectedSubscription} />
              )}
            </div>
          </div>
        )}

        {/* Meal Swap Modal */}
        {showSwapModal && mealToSwap && selectedSubscription && (
          <MealSwapModal
            isOpen={showSwapModal}
            onClose={() => setShowSwapModal(false)}
            subscriptionId={selectedSubscription.id}
            currentMeal={mealToSwap}
            weekStartDate={selectedSubscription.current_week_plan?.week_start_date}
            onSwapSuccess={handleSwapSuccess}
          />
        )}
      </div>

      <AppFooter />
    </div>
  );
};

export default Subscriptions;
